import { useCallback, useEffect, useState } from 'react';

import {
  notificationApi,
  NotificationApplication,
  NotificationChannel,
  Topic,
  SubscriptionList,
  ProfileSection,
  ContactsSection,
  NotificationServiceContextProvider,
} from '@/core/notifications/export';
import { useIsMobile } from '@/shared/hooks/export';
import { Loading } from '@/shared/components/export';
import { NotificationsSubTab } from '@/core/widget-navigation/export';
import TabButtonRow from '@/core/settings/components/tab-button-row';
import '@/shared/theme/notifications-settings.scss';

const TABS: { key: NotificationsSubTab; label: string }[] = [
  { key: 'subscriptions', label: 'Alarms' },
  { key: 'profile', label: 'Profile' },
  { key: 'contacts', label: 'Contacts' },
];

export default function NotificationsTab({
  subtab,
  onSubtabChange,
}: {
  subtab: NotificationsSubTab;
  onSubtabChange: (subtab: NotificationsSubTab) => void;
}) {
  const isMobile = useIsMobile(576);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [applications, setApplications] = useState<NotificationApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadCatalog = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    Promise.all([
      notificationApi.getTopics(),
      notificationApi.getChannels(),
      notificationApi.getApplications(),
    ])
      .then(([loadedTopics, loadedChannels, loadedApplications]) => {
        setTopics(loadedTopics);
        setChannels(loadedChannels);
        setApplications(loadedApplications);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const content = !loading && !loadFailed && (
    <>
      {subtab === 'subscriptions' && (
        <SubscriptionList topics={topics} channels={channels} applications={applications} />
      )}
      {subtab === 'profile' && <ProfileSection />}
      {subtab === 'contacts' && <ContactsSection channels={channels} />}
    </>
  );

  const loadingOrError = (
    <>
      {loading && (
        <div className="p-4">
          <Loading type="bar" />
        </div>
      )}
      {!loading && loadFailed && (
        <div className="text-center p-4">
          <p style={{ color: 'var(--bs-error)' }}>Could not load the notification service.</p>
          <button className="btn btn-sm btn-outline-secondary" onClick={loadCatalog}>
            Retry
          </button>
        </div>
      )}
    </>
  );

  return (
    <NotificationServiceContextProvider>
      <div
        className="notifications-settings d-flex flex-column"
        style={{ background: 'var(--bs-body-bg)', flex: '1 1 auto' }}
      >
        {isMobile ? (
          <div className="d-flex flex-column flex-grow-1">
            <div className="px-3 py-2" style={{ background: 'var(--bs-primary-200)' }}>
              <select
                className="form-select form-select-sm"
                value={subtab}
                onChange={e => onSubtabChange(e.target.value as NotificationsSubTab)}
              >
                {TABS.map(t => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {loadingOrError}
              {content}
            </div>
          </div>
        ) : (
          <div className="d-flex flex-grow-1" style={{ gap: 24 }}>
            <TabButtonRow
              items={TABS}
              activeKey={subtab}
              onSelect={onSubtabChange}
              orientation="vertical"
            />

            <div className="flex-grow-1" style={{ minWidth: 0, paddingRight: 24 }}>
              {loadingOrError}
              {content}
            </div>
          </div>
        )}
      </div>
    </NotificationServiceContextProvider>
  );
}
